import type { MapPlotOptions } from '../../../PageConfigProvider/types/item';
import type { MetricValues } from '../../../PageConfigProvider/types/metric';
import { LocationType, type MapPoint } from '../types';
import { countryNameToIsoCode } from '../utils/country-codes';
import { normalizePointValue } from '../utils/normalization';
import type { ChoroplethLayerConfig } from './ChoroplethConfig';

export type ValueByLayerId = Map<string, Record<string, number>>;
export type TooltipByLayerId = Map<
  string,
  Record<string, Record<string, string | number>>
>;

export type Extent = { min: number; max: number };
export type LayerValues = Record<LocationType, number[]>;
export type LayerStatsByLocation = Record<
  LocationType,
  { values: number[]; extent: Extent | null }
>;

export interface ChoroplethDataOptions {
  metric?: MetricValues;
  mapOptions?: MapPlotOptions;
}

export interface ChoroplethDataResult {
  values: {
    byLayer: ValueByLayerId;
    tooltips: TooltipByLayerId;
  };

  layers: LayerStatsByLocation;
}

export type IngestResult = {
  valueByLayerId: ValueByLayerId;
  tooltipByLayerId: TooltipByLayerId;
  layerValues: LayerValues;
};

export function normalizeName(rawName: string): string {
  let name = rawName?.trim() || '';
  if (name.endsWith('Non-Metro')) {
    name = name.replace(/Non-Metro$/, '').trim();
  }
  if (name.endsWith('NonMetro')) {
    name = name.replace(/NonMetro$/, '').trim();
  }
  if (name.endsWith(', United States')) {
    name = name.replace(/, United States$/, '').trim();
  }
  return name;
}

export function normalizeChoroplethData(
  points: MapPoint[],
  configs: ChoroplethLayerConfig[],
  options?: ChoroplethDataOptions
): ChoroplethDataResult {
  const { metric, mapOptions } = options ?? {};

  const { valueByLayerId, tooltipByLayerId, layerValues } = ingestPoints(
    points,
    configs,
    metric,
    mapOptions
  );

  return {
    values: {
      byLayer: valueByLayerId,
      tooltips: tooltipByLayerId,
    },
    layers: buildLayerStats(layerValues),
  };
}

export function computeExtent(
  points: MapPoint[]
): { min: number; max: number } | null {
  const values = points
    .map((p) => p.value)
    .filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
  if (!values.length) return null;
  return { min: Math.min(...values), max: Math.max(...values) };
}

function ingestPoints(
  points: MapPoint[],
  configs: ChoroplethLayerConfig[],
  metric?: MetricValues,
  mapOptions?: MapPlotOptions
): IngestResult {
  const valueByLayerId: ValueByLayerId = new Map();
  const tooltipByLayerId: TooltipByLayerId = new Map();

  configs.forEach((config) => {
    valueByLayerId.set(config.id, {});
    tooltipByLayerId.set(config.id, {});
  });

  const layerValues = createEmptyLayerValues();

  for (const point of points) {
    const config = configs.find((c) => c.isApplicable(point));
    if (!config) continue;

    if (point.value === undefined || point.value === null) continue;

    const valueMap = valueByLayerId.get(config.id);
    const tooltipMap = tooltipByLayerId.get(config.id);
    if (!valueMap || !tooltipMap) continue;

    const label = normalizeName(point.label);
    const keyName = (() => {
      if (
        config.id === LocationType.METRO_AREA ||
        config.id === LocationType.CITY ||
        config.id === LocationType.STATE ||
        config.id === LocationType.COUNTY
      ) {
        return String(point.id);
      }
      if (config.id === LocationType.COUNTRY) {
        const iso = countryNameToIsoCode[label.toLowerCase()];
        return iso ?? label;
      }
      return label;
    })();

    const { value } = normalizePointValue(
      point.value,
      metric,
      point.locationType,
      String(point.id),
      mapOptions,
      point.label
    );

    if (typeof value === 'number' && Number.isFinite(value)) {
      valueMap[keyName] = value;
      layerValues[config.id].push(value);
    }
    if (point.tooltip) {
      tooltipMap[keyName] = { ...point.tooltip };
    }
  }

  return {
    valueByLayerId,
    tooltipByLayerId,
    layerValues,
  };
}

function createEmptyLayerValues(): LayerValues {
  return {
    [LocationType.REGION]: [],
    [LocationType.COUNTRY]: [],
    [LocationType.STATE]: [],
    [LocationType.COUNTY]: [],
    [LocationType.METRO_AREA]: [],
    [LocationType.CITY]: [],
  };
}

function buildLayerStats(layerValues: LayerValues): LayerStatsByLocation {
  const computeExtentFrom = (vals: number[]): Extent | null =>
    vals.length ? { min: Math.min(...vals), max: Math.max(...vals) } : null;

  return {
    [LocationType.REGION]: {
      values: layerValues[LocationType.REGION],
      extent: computeExtentFrom(layerValues[LocationType.REGION]),
    },
    [LocationType.COUNTRY]: {
      values: layerValues[LocationType.COUNTRY],
      extent: computeExtentFrom(layerValues[LocationType.COUNTRY]),
    },
    [LocationType.STATE]: {
      values: layerValues[LocationType.STATE],
      extent: computeExtentFrom(layerValues[LocationType.STATE]),
    },
    [LocationType.COUNTY]: {
      values: layerValues[LocationType.COUNTY],
      extent: computeExtentFrom(layerValues[LocationType.COUNTY]),
    },
    [LocationType.METRO_AREA]: {
      values: layerValues[LocationType.METRO_AREA],
      extent: computeExtentFrom(layerValues[LocationType.METRO_AREA]),
    },
    [LocationType.CITY]: {
      values: layerValues[LocationType.CITY],
      extent: computeExtentFrom(layerValues[LocationType.CITY]),
    },
  };
}
